// "Sample Pickle Pack" — fixed, admin-curated contents the customer cannot
// modify (no checkboxes, no size picker — every product is 150g). Every
// product has its own admin-set price (different pickles cost different
// amounts to make), and the pack total is the sum of ALL of them since the
// customer can't deselect any. Capped at pack.maxQtyPerOrder (default 3) per
// pack type, enforced here, again in CartSheet's quantity stepper, and again
// server-side in migration 030's create_checkout_order sample_pack branch
// (the real source of truth).
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatPrice, validateSamplePackQuantity } from "@/lib/cart";
import { track } from "@/lib/analytics";

export const SamplePackModal = ({ open, packType, onClose, onAdded, samplePackSettings, products, cart }) => {
  if (!packType) return null;
  const pack = samplePackSettings.packs[packType];
  const packProducts = (products || []).filter((p) => p.id in pack.productPrices);
  // Sum only real products currently present in the catalog. If a product was
  // deleted but still exists in pack settings, we must not charge for it.
  const totalPrice = packProducts.reduce(
    (sum, p) => sum + (Number(pack.productPrices[p.id]) || 0),
    0
  );
  const currentQty = cart.items
    .filter((i) => i.item_type === "sample_pack" && i.pack_type === packType)
    .reduce((sum, i) => sum + i.quantity, 0);
  const atCap = !validateSamplePackQuantity(cart.items, packType, 1, pack.maxQtyPerOrder);

  const handleAdd = () => {
    if (atCap) return;
    cart.addSamplePackItem(pack, packType, totalPrice);
    track("sample_pack_added_to_cart", {
      pack_type: packType,
      price: totalPrice,
      quantity_in_cart_after: currentQty + 1,
    });
    toast.success(`${pack.label} added to cart`, { description: formatPrice(totalPrice) });
    onClose();
    onAdded?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="sample-pack-modal"
        className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-md w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto"
      >
        <DialogTitle className="flex items-center gap-2 font-display text-xl text-[#5A2D1D]">
          <span className="inline-flex p-1.5 rounded-sm bg-[#556B2F]/10 text-[#556B2F]">
            <Gift className="w-4 h-4" />
          </span>
          {pack.label}
        </DialogTitle>

        <div className="flex items-center justify-between mt-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#556B2F] bg-[#556B2F]/10 px-2.5 py-1 rounded-full">
            {pack.fixedSizeLabel || "150g"} each
          </span>
          <span data-testid="sample-pack-total" className="font-display text-lg font-bold text-[#5A2D1D]">
            {formatPrice(totalPrice)}
          </span>
        </div>

        {packProducts.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#2E1A12]/60">
            No products are configured for {pack.label} yet.
          </div>
        ) : (
          <div className="mt-3 space-y-2" data-testid="sample-pack-product-list">
            {packProducts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-sm border border-[#5A2D1D]/15 bg-white/40">
                <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded-sm object-cover flex-shrink-0" />
                <span className="flex-1 text-sm font-semibold text-[#2E1A12] truncate">{p.name}</span>
                <span className="text-xs text-[#2E1A12]/50 flex-shrink-0">{pack.fixedSizeLabel || "150g"}</span>
                <span className="text-xs font-bold text-[#556B2F] flex-shrink-0">{formatPrice(pack.productPrices[p.id])}</span>
              </div>
            ))}
          </div>
        )}

        {currentQty > 0 && (
          <p className="mt-3 text-xs text-[#2E1A12]/60">
            You already have {currentQty} of {pack.maxQtyPerOrder} {pack.label} in your cart.
          </p>
        )}

        <button
          type="button"
          data-testid="sample-pack-add-to-cart"
          disabled={atCap || packProducts.length === 0}
          onClick={handleAdd}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 disabled:cursor-not-allowed text-[#F5E6CC] py-3 rounded-sm font-bold transition-colors"
        >
          {atCap ? `Maximum ${pack.maxQtyPerOrder} per order already in cart` : `Add to Cart — ${formatPrice(totalPrice)}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
